import React, { useRef, useState, Fragment } from 'react';
import { graphql } from 'react-apollo';
import PropTypes from 'prop-types';

import Alert from '../Alert';
import CustomControl from '../CustomControl';
import Button from '../Button';
import { getErrorString } from '../../../util/normalize-errors';
import { reportedPhotosCreate as reportedPhotosCreateMutation } from '../../../graphql/mutations/photos';
import Spinner from '../Spinner';
import WarningIcon from '../../icons/WarningIcon';
import withModal from '../modal/withModal';
import { REPORT_PHOTO_MODAL } from '../../../constants/modals';
import FormGroup from '../FormGroup';

const propTypes = {
  actionProps: PropTypes.shape({
    albumId: PropTypes.string,
    photoId: PropTypes.string.isRequired,
  }).isRequired,
  onRequestClose: PropTypes.func.isRequired,
  reportedPhotosCreate: PropTypes.func.isRequired,
};

const REPORT_REASON = {
  FEEDBACK: 'FEEDBACK',
  TECHNICAL_ISSUE: 'TECHNICAL_ISSUE',
  INAPPROPRIATE_PHOTO: 'INAPPROPRIATE_PHOTO',
};

// todo: import modal const and export as function caller that passes action props

const ReportPhotoModal = ({
  actionProps,
  onRequestClose,
  reportedPhotosCreate,
}) => {
  const notesRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportReason, setReportReason] = useState(REPORT_REASON.FEEDBACK);
  const [success, setSuccess] = useState(false);

  if (!actionProps) {
    return null;
  }

  const { albumId, photoId } = actionProps;

  const onSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const notes = notesRef.current?.value;
      await reportedPhotosCreate({
        albumId: albumId || undefined,
        photoId,
        notes,
        reportReason,
      });

      setSuccess(true);
    } catch (createError) {
      setError(getErrorString(createError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="report-photo-modal-content">
      <h3 className="">
        <WarningIcon color="#fe5851" size="19" />
        &nbsp;Report a problem
      </h3>

      {!success && (
        <Fragment>
          <div className="my-3 report-reasons">
            <CustomControl
              checked={reportReason === REPORT_REASON.FEEDBACK}
              id="report-reason-general-feedback"
              name="report-reason"
              label="General Feedback"
              labelNote="Tell us about your Waldo Photos experience."
              onChange={() => setReportReason(REPORT_REASON.FEEDBACK)}
              type="radio"
            />
            <CustomControl
              checked={reportReason === REPORT_REASON.TECHNICAL_ISSUE}
              id="report-reason-not-working"
              name="report-reason"
              label="Something isn't working"
              labelNote="Something about this photo is not working properly."
              onChange={() => setReportReason(REPORT_REASON.TECHNICAL_ISSUE)}
              type="radio"
            />
            <CustomControl
              checked={reportReason === REPORT_REASON.INAPPROPRIATE_PHOTO}
              id="report-reason-innapropriate"
              name="report-reason"
              label="Report as inappropriate"
              labelNote="This photo is offensive or inappropriate."
              onChange={() =>
                setReportReason(REPORT_REASON.INAPPROPRIATE_PHOTO)
              }
              type="radio"
            />
          </div>

          <FormGroup
            type="textarea"
            label="Additional Notes"
            className="text-sm"
            id="report-photo-notes"
            inputRef={notesRef}
            name="reportPhotoNotes"
            placeholder="Please include any additional notes or information you would like to be passed on that explain the reason for your decision to report this photo."
            rows={5}
          />

          <Alert className="mt-4" type="info">
            If you report a photo, your report will be anonymous to the album
            owner and other viewers.
          </Alert>

          <div className="action-buttons mt-3">
            <Button
              btnStyle="gray"
              className="cancel-button mr-2"
              onClick={onRequestClose}
            >
              Cancel
            </Button>
            <Button
              btnStyle="danger"
              className="danger"
              disabled={loading}
              onClick={onSubmit}
            >
              {loading ? <Spinner light size="xs" /> : 'Report'}
            </Button>
          </div>

          {error && (
            <Alert className="mt-3" type="danger">
              {error}
            </Alert>
          )}
        </Fragment>
      )}

      {success && (
        <Fragment>
          <h4>Thanks for letting us know.</h4>
          <p>Our team will review your report as soon as possible.</p>

          <Button onClick={onRequestClose} className="align-right">
            Done
          </Button>
        </Fragment>
      )}
    </div>
  );
};

ReportPhotoModal.propTypes = propTypes;

const withReportedPhotosCreate = graphql(reportedPhotosCreateMutation, {
  props: ({ mutate }) => ({
    reportedPhotosCreate: ({ photoId, notes, reportReason, albumId }) =>
      mutate({
        variables: {
          input: {
            photos: [
              {
                album_id: albumId,
                notes,
                photo_id: photoId,
                reason: reportReason,
              },
            ],
          },
        },
      }),
  }),
});

export default withModal(withReportedPhotosCreate(ReportPhotoModal), {
  className: 'report-photo-modal',
  hideHeader: true,
  name: REPORT_PHOTO_MODAL,
  size: 'sm',
});
